import { useCallback, useEffect } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { useTheme, Box } from "@mui/material"
import _ from "lodash"

import { useNormalizedRouterSearchParams } from "hooks"
import { useMap } from "hooks/map/useMap"
import { useOrdinalMap } from "hooks/ordinal"
import {
  useFlyToMap,
  useGetUserLocationView,
  useGetMapViewFromUrl,
} from "hooks/map"
import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"
import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { pluralize } from "utils/string"
import { SearchToggleButton } from "components/domains/search/SearchToggleButton"
import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { AppNavigation as DefaultAppNavigation } from "containers/AppNavigation"
import { MyLocationButton } from "components/Button"
import { convertMapViewObjectToPath } from "utils/map"
import { OrdinalSelector } from "extensions/iconsiam/components/ordinal-selector"
import AttractionButton from "extensions/iconsiam/components/domains/attractions/MobileAttractionButton"
import { Q_VENUE } from "constant"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { HandlerFn, HandlerFnResultType } from "maptalks/dist/core/Eventable"

export const MapOrdinal = ({ AppNavigation = DefaultAppNavigation }) => {
  const { isLoading, userLocation, locationVenue } = useGeoLocation()
  const navigate = useNavigate()
  const { ordinal } = useParams()
  const [searchParams] = useSearchParams()
  const { userLocation: userLocationParams } = useNormalizedRouterSearchParams<{
    userLocation: string
  }>()
  const { t } = useTranslation()
  const { isMultiOrdinalVenue } = useVenue()
  const {
    viewingVenueId,
    viewingVenueView,
    changeViewingVenue,
    quickSearchCategories,
    locationVenueId,
    locationOrdinal,
    sortedLevels,
  } = useMap()
  const { openSearch, isKioskMode } = useAppUI()
  const { indoorRef, mapLoaded: indoorMapLoaded } = useIndoorMap()

  const parsedOrdinal = Number(ordinal)
  const hasUserLocationAtCurrentFloor = locationOrdinal === parsedOrdinal

  useOrdinalMap(parsedOrdinal)
  const userLocationView = useGetUserLocationView()
  const { mapView } = useGetMapViewFromUrl()
  useFlyToMap(mapView || viewingVenueView)

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components[`AppMap`] || {}

  const {
    searchBarContainer,
    toolbarContainer,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    locationButtonContainer,
    ordinalSelector,
    navigationContainer,
    attractionButton,
  } = styleOverrides

  /** Auto set viewingVenue to closest venue when map moved */
  const onVenueMoveIntoView = useCallback<HandlerFn>(
    ({
      venueId,
    }: HandlerFnResultType & { venueId: string; distance: number }) => {
      changeViewingVenue(venueId)
    },
    [changeViewingVenue]
  )
  useEffect(() => {
    const indoorMap = indoorRef.current
    if (indoorMapLoaded && indoorMap) {
      indoorMap.detectVenueInView = true
      indoorMap.map.on("venue:venuemoveintoview", onVenueMoveIntoView)
    }
    return () => {
      if (indoorMap) {
        indoorMap.map.off("venue:venuemoveintoview", () => {})
        indoorMap.detectVenueInView = false
      }
    }
  }, [indoorRef, indoorMapLoaded, onVenueMoveIntoView])

  const onClickOrdinal = useCallback(
    (ordinal, venueId) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)

      const ordinalPath = ordinal !== null ? `ordinal/${ordinal}` : ""
      const search = searchParams.size > 0 ? `?${searchParams.toString()}` : ""

      navigate(`/maps/${ordinalPath}${search}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const handleClickQuickSearch = useCallback(
    (featureType, amenityCategory) => {
      //This condition prevent navigation when ordinal or viewingVenueId is undefined
      if (_.isNil(ordinal) || _.isNil(viewingVenueId)) return
      const pluralType = pluralize(featureType)
      navigate(
        `/maps/${pluralType}/${amenityCategory}?from-ordinal=${ordinal}&venue=${viewingVenueId}`
      )
    },
    [navigate, ordinal, viewingVenueId]
  )

  const handleClickMyLocation = useCallback(() => {
    //This condition prevent navigation when ordinal is undefined
    if (_.isNil(ordinal) || !userLocation) return
    const userOrdinal = _.get(userLocation, "properties.ordinal", 0)
    const mapViewPath = convertMapViewObjectToPath(userLocationView)
    navigate(`/maps/ordinal/${userOrdinal}?mv=${mapViewPath}`, {
      replace: true,
    })
  }, [navigate, ordinal, userLocationView, userLocation])

  useEffect(() => {
    if (
      !isLoading &&
      userLocationParams &&
      userLocation &&
      !_.isNil(locationVenue?.id)
    ) {
      const {
        properties: { ordinal: userOrdinal },
      } = userLocation
      navigate(`/maps/ordinal/${userOrdinal}?venue=${locationVenue?.id}`, {
        replace: true,
      })
    } else if (
      !isLoading &&
      userLocationParams &&
      !_.isNil(locationVenue?.id)
    ) {
      navigate(`/maps/ordinal/${ordinal}?venue=${locationVenue?.id}`, {
        replace: true,
      })
    }
  }, [
    navigate,
    ordinal,
    isLoading,
    userLocation,
    locationVenue,
    userLocationParams,
  ])

  useEffect(() => {
    if (hasUserLocationAtCurrentFloor) handleClickMyLocation()
  }, [hasUserLocationAtCurrentFloor, handleClickMyLocation])

  return (
    <>
      <Box sx={toolbarContainer}>
        {!isKioskMode && (
          <Box sx={searchBarContainer}>
            <SearchToggleButton
              placeholder={t("Search for your destination")}
              onClick={openSearch}
            />
          </Box>
        )}
        <Box sx={quickLinkContainer}>
          <FeatureChipToggleBar
            categories={quickSearchCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
      </Box>
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <OrdinalSelector
            selectedOrdinal={parsedOrdinal}
            levels={sortedLevels}
            onClickOrdinal={onClickOrdinal}
            currentVenue={locationVenueId}
            viewingVenueId={viewingVenueId}
            locationOrdinal={locationOrdinal}
          />
        </Box>
      )}
      <Box sx={attractionButton}>
        <AttractionButton />
      </Box>
      {userLocation && (
        <Box sx={locationButtonContainer}>
          <MyLocationButton onClick={handleClickMyLocation} />
        </Box>
      )}
      <Box sx={navigationContainer}>
        <AppNavigation />
      </Box>
    </>
  )
}
